import { useEffect, useState } from 'react';
import { Card } from '@ftedn/ui';
import { useApi } from '../../hooks/useApi';

interface EmailStats {
  todaySends: number;
  hardCap: number;
  percentUsed: number;
  circuitBreaker: boolean;
  suppressionCount: number;
  recentBounces: Array<{ email: string; reason: string; at: string }>;
}

export function EmailPage() {
  const { get, post } = useApi();
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState<string | null>(null);

  useEffect(() => {
    get('/email/stats')
      .then(setStats)
      .catch((e: unknown) => setError(String(e)));
  }, []);

  const toggleBreaker = async () => {
    if (!stats) return;
    setToggling(true);
    try {
      await post('/email/circuit-breaker', { enabled: !stats.circuitBreaker });
      setStats({ ...stats, circuitBreaker: !stats.circuitBreaker });
    } catch (e) { setError(String(e)); }
    setToggling(false);
  };

  const searchSuppression = async () => {
    if (!searchEmail.trim()) return;
    try {
      const result = await get(`/email/suppressions/${encodeURIComponent(searchEmail)}`);
      setSearchResult(result.suppressed ? `${searchEmail} is SUPPRESSED (${result.reason})` : `${searchEmail} is NOT suppressed`);
    } catch (e) { setSearchResult(String(e)); }
  };

  if (error) return <Card><p style={{ color: 'var(--color-error)' }}>Error: {error}</p></Card>;
  if (!stats) return <Card><p>Loading email stats…</p></Card>;

  const capColor = stats.percentUsed >= 0.8
    ? stats.percentUsed >= 1 ? 'var(--color-error)' : 'var(--color-warning, #f59e0b)'
    : 'var(--color-success, #10b981)';

  return (
    <div>
      <h1>📧 Email Management</h1>

      {/* Volume & Cap */}
      <Card>
        <h2>Today's Volume</h2>
        <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem' }}>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 700, color: capColor }}>
              {stats.todaySends.toLocaleString()}
            </p>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              of {stats.hardCap.toLocaleString()} cap
            </p>
          </div>
          <div>
            <p style={{ fontSize: '2rem', fontWeight: 700 }}>
              {Math.round(stats.percentUsed * 100)}%
            </p>
            <p style={{ color: 'var(--color-text-secondary)' }}>used</p>
          </div>
        </div>
        <div style={{
          height: 8, borderRadius: 4, background: 'var(--color-surface-2, #e5e7eb)',
          marginTop: '1rem', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', width: `${Math.min(stats.percentUsed * 100, 100)}%`,
            background: capColor, borderRadius: 4, transition: 'width 0.3s',
          }} />
        </div>
      </Card>

      {/* Circuit Breaker */}
      <Card>
        <h2>Circuit Breaker</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
          <span style={{
            display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
            background: stats.circuitBreaker ? 'var(--color-success, #10b981)' : 'var(--color-error, #ef4444)',
          }} />
          <span>{stats.circuitBreaker ? 'ENABLED (sends active)' : 'DISABLED (sends halted)'}</span>
          <button
            onClick={toggleBreaker}
            disabled={toggling}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: stats.circuitBreaker ? 'var(--color-error, #ef4444)' : 'var(--color-success, #10b981)',
              color: '#fff', fontWeight: 600,
            }}
          >
            {toggling ? '…' : stats.circuitBreaker ? 'Disable Email' : 'Enable Email'}
          </button>
        </div>
      </Card>

      {/* Suppressions */}
      <Card>
        <h2>Suppressions ({stats.suppressionCount})</h2>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <input
            type="email"
            placeholder="Search email..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 6,
              border: '1px solid var(--color-border, #d1d5db)',
              background: 'var(--color-surface, #fff)',
            }}
          />
          <button
            onClick={searchSuppression}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none',
              background: 'var(--color-primary, #18181b)', color: '#fff',
              cursor: 'pointer', fontWeight: 600,
            }}
          >
            Search
          </button>
        </div>
        {searchResult && <p style={{ marginTop: '0.5rem' }}>{searchResult}</p>}
      </Card>

      {/* Recent Bounces */}
      <Card>
        <h2>Recent Bounces</h2>
        {stats.recentBounces.length === 0 ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>No recent bounces ✓</p>
        ) : (
          <ul>
            {stats.recentBounces.map((b, i) => (
              <li key={i}>
                <strong>{b.email}</strong> — {b.reason} ({b.at})
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
